import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { API_CONFIG } from '../config/api';
import { getOAuthConfig } from '../config/oauthConfig';
import { clearEmployeeNotFoundFlag, hasEmployeeNotFoundFlag } from '../contexts/AuthContext';
import { handleApiError } from '../lib/errors';
import { logger } from '../utils/logger';

const oauthConfigQueryKey = ['oauth', 'config'] as const;
const OAUTH_CONFIG_STALE_MS = 5 * 60 * 1000;

const LandingPage: React.FC = () => {
  const { refetch, isPending, isFetching } = useQuery({
    queryKey: oauthConfigQueryKey,
    queryFn: getOAuthConfig,
    enabled: true,
    staleTime: OAUTH_CONFIG_STALE_MS,
  });
  const isOAuthBusy = isPending || isFetching;
  const [showEmployeeNotFoundMessage, setShowEmployeeNotFoundMessage] = useState(false);

  useEffect(() => {
    if (hasEmployeeNotFoundFlag()) {
      setShowEmployeeNotFoundMessage(true);
    }
  }, []);

  const handleTryAgain = () => {
    clearEmployeeNotFoundFlag();
    setShowEmployeeNotFoundMessage(false);
  };

  const handleLogin = () => {
    void refetch().then((result) => {
      if (!result.isSuccess || !result.data) {
        if (result.error) {
          logger.error('Failed to load OAuth configuration:', result.error);
          const errorMessage = handleApiError(result.error);
          toast.error(
            errorMessage || 'Failed to load authentication configuration. Please contact support.'
          );
        }
        return;
      }

      const oauthConfig = result.data;
      const { tenantId, clientId, redirectUri } = oauthConfig;

      if (!tenantId) {
        logger.error('ERROR: Tenant ID is not configured!');
        toast.error('Tenant ID is not configured. Please contact support.');
        return;
      }

      if (!clientId) {
        logger.error('ERROR: Microsoft OAuth Client ID is not configured!');
        toast.error('Client ID is not configured. Please contact support.');
        return;
      }

      if (!redirectUri) {
        logger.error('ERROR: Redirect URI is not configured!');
        toast.error('Redirect URI is not configured. Please contact support.');
        return;
      }

      const isDevelopment =
        API_CONFIG.baseURL.includes('localhost') || API_CONFIG.baseURL.includes('127.0.0.1');
      if (
        !isDevelopment &&
        (redirectUri.includes('localhost') || redirectUri.includes('127.0.0.1'))
      ) {
        logger.error('ERROR: Redirect URI contains localhost:', redirectUri);
        toast.error(
          'Invalid redirect URI configuration. Redirect URI cannot contain localhost in production.'
        );
        return;
      }

      logger.debug('OAuth Configuration loaded:', {
        tenantId: tenantId,
        clientId: clientId ? `${clientId.substring(0, 8)}...` : 'NOT SET',
        redirectUri: redirectUri,
      });

      const encodedRedirectUri = encodeURIComponent(redirectUri);
      const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodedRedirectUri}&scope=openid%20profile&state=1234`;

      window.location.href = authUrl;
    });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-neutral-200 via-neutral-400 to-neutral-500 p-5">
      <div className="relative z-10 w-full max-w-lg">
        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-8 w-[200px] -translate-x-1/2 opacity-80">
          <img
            src="/assets/logo/PPE-intelligence_Logo_rgb_Black.svg"
            alt="PPE Intelligence Logo"
            className="block h-auto w-full"
          />
        </div>
        <div className="rounded-br-2xl rounded-tl-2xl border border-warning-500/20 bg-white p-12 text-center shadow-lg backdrop-blur-sm">
          <h1 className="mb-3 text-3xl font-bold text-text-primary">Admin App</h1>
          {showEmployeeNotFoundMessage ? (
            <Alert
              variant="default"
              className="mb-6 border-warning-500 bg-warning-500/10 text-left"
            >
              <AlertDescription className="text-warning-600">
                Your account is not registered in the system. Please contact support to request
                access.
              </AlertDescription>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Button
                  type="button"
                  onClick={() => {
                    window.location.href = '/contact-support';
                  }}
                >
                  Contact Support
                </Button>
                <Button type="button" variant="outline" onClick={handleTryAgain}>
                  Try Again
                </Button>
              </div>
            </Alert>
          ) : (
            <>
              <p className="mb-8 text-text-secondary">Sign in to access your Admin App</p>
              <Button
                type="button"
                className="w-full justify-center gap-3 h-15 bg-blue-600"
                onClick={handleLogin}
                disabled={isOAuthBusy}
                aria-label="Sign in with Microsoft"
              >
                {isOAuthBusy ? (
                  <>
                    <Spinner data-icon="inline-start" />
                    Loading...
                  </>
                ) : (
                  <>
                    <svg
                      className="size-5"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <rect x="0" y="0" width="11" height="11" fill="#F25022" />
                      <rect x="12" y="0" width="11" height="11" fill="#7FBA00" />
                      <rect x="0" y="12" width="11" height="11" fill="#00A4EF" />
                      <rect x="12" y="12" width="11" height="11" fill="#FFB900" />
                    </svg>
                    Sign in with Microsoft
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
